var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Type } from 'class-transformer';
import { ArrayMaxSize, ArrayMinSize, IsArray, IsBoolean, IsEnum, IsInt, IsOptional, IsString, IsUUID, Matches, Min, ValidateNested, } from 'class-validator';
import { TreatmentLocation } from '../../calls/enums/treatment-location.enum.js';
import { LOCAL_ISO_REGEX, RRULE_REGEX } from '../constants.js';
export class CreateAppointmentSeriesParticipantDto {
    patientId;
}
__decorate([
    IsUUID(),
    __metadata("design:type", String)
], CreateAppointmentSeriesParticipantDto.prototype, "patientId", void 0);
/**
 * Create a recurring appointment series.
 *
 * The `rrule` is the source of truth for which dates the series occurs on.
 * `dtstartLocal` is a local-time ISO string (no offset) interpreted in `tzid`.
 * For TP-linked series, `expectedOccurrenceCount` is required and must match
 * the rrule's COUNT (or computed UNTIL → COUNT) — the treatment plan reserves
 * that many units up-front.
 *
 * `confirmConflicts` MUST be set to `true` when the caller has already seen
 * the soft-warn conflict report and chooses to proceed anyway. Set to `false`
 * (or omitted) to make the server fail fast if conflicts are detected, so the
 * frontend can surface the report.
 */
export class CreateAppointmentSeriesDto {
    rrule;
    dtstartLocal;
    tzid;
    expectedOccurrenceCount;
    treatmentPlanId;
    defaultTherapistId;
    defaultRoomId;
    defaultOfficeLocationId;
    defaultOfficeProductAssignmentId;
    defaultTreatmentLocation;
    defaultPlannedDuration;
    defaultService;
    defaultDescription;
    defaultDeviceIds;
    defaultBillableServiceItemIds;
    defaultParticipants;
    confirmConflicts;
}
__decorate([
    IsString(),
    Matches(RRULE_REGEX, {
        message: 'rrule must be a valid RFC 5545 RRULE string',
    }),
    __metadata("design:type", String)
], CreateAppointmentSeriesDto.prototype, "rrule", void 0);
__decorate([
    Matches(LOCAL_ISO_REGEX, {
        message: 'dtstartLocal must be local-time ISO `YYYY-MM-DDTHH:mm:ss` (no offset)',
    }),
    __metadata("design:type", String)
], CreateAppointmentSeriesDto.prototype, "dtstartLocal", void 0);
__decorate([
    IsString(),
    IsOptional(),
    __metadata("design:type", String)
], CreateAppointmentSeriesDto.prototype, "tzid", void 0);
__decorate([
    IsInt(),
    Min(1),
    IsOptional(),
    __metadata("design:type", Number)
], CreateAppointmentSeriesDto.prototype, "expectedOccurrenceCount", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    __metadata("design:type", Object)
], CreateAppointmentSeriesDto.prototype, "treatmentPlanId", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    __metadata("design:type", Object)
], CreateAppointmentSeriesDto.prototype, "defaultTherapistId", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    __metadata("design:type", Object)
], CreateAppointmentSeriesDto.prototype, "defaultRoomId", void 0);
__decorate([
    IsUUID(),
    __metadata("design:type", String)
], CreateAppointmentSeriesDto.prototype, "defaultOfficeLocationId", void 0);
__decorate([
    IsUUID(),
    IsOptional(),
    __metadata("design:type", Object)
], CreateAppointmentSeriesDto.prototype, "defaultOfficeProductAssignmentId", void 0);
__decorate([
    IsEnum(TreatmentLocation),
    __metadata("design:type", String)
], CreateAppointmentSeriesDto.prototype, "defaultTreatmentLocation", void 0);
__decorate([
    IsInt(),
    Min(1),
    __metadata("design:type", Number)
], CreateAppointmentSeriesDto.prototype, "defaultPlannedDuration", void 0);
__decorate([
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], CreateAppointmentSeriesDto.prototype, "defaultService", void 0);
__decorate([
    IsString(),
    IsOptional(),
    __metadata("design:type", Object)
], CreateAppointmentSeriesDto.prototype, "defaultDescription", void 0);
__decorate([
    IsArray(),
    IsUUID('4', { each: true }),
    IsOptional(),
    __metadata("design:type", Array)
], CreateAppointmentSeriesDto.prototype, "defaultDeviceIds", void 0);
__decorate([
    IsArray(),
    IsUUID('4', { each: true }),
    IsOptional(),
    __metadata("design:type", Array)
], CreateAppointmentSeriesDto.prototype, "defaultBillableServiceItemIds", void 0);
__decorate([
    IsArray(),
    ArrayMinSize(1),
    ArrayMaxSize(5),
    ValidateNested({ each: true }),
    Type(() => CreateAppointmentSeriesParticipantDto),
    __metadata("design:type", Array)
], CreateAppointmentSeriesDto.prototype, "defaultParticipants", void 0);
__decorate([
    IsBoolean(),
    IsOptional(),
    __metadata("design:type", Boolean)
], CreateAppointmentSeriesDto.prototype, "confirmConflicts", void 0);
//# sourceMappingURL=create-appointment-series.dto.js.map